import './adminPanel.css';
import '../UI/ui.css';
import { authStore } from '../../store/auth.store.js';
import { leaderboardStore } from '../../store/leaderboard.store.js';
import { createModal } from '../UI/Modal.js';
import { showToast } from '../UI/Toast.js';
import { addMember, updateCredits, resetLeaderboard, } from '../../api/leaderboard.js';
import { createSeason, updateSeason } from '../../api/season.js';
import { validateName, validateCredits, validateDelta, } from '../../utils/validators.js';
// ── Admin Bar ─────────────────────────────────────────────────────────────────
export function createAdminBar() {
    const bar = document.createElement('div');
    const render = () => {
        const profile = authStore.getProfile();
        if (!profile || !authStore.canAdmin()) {
            bar.innerHTML = '';
            return;
        }
        const roleLabel = profile.role === 'president' ? 'President' : 'Vice President';
        const roleClass = `role-${profile.role}`;
        bar.innerHTML = `
      <div class="admin-bar">
        <div class="admin-bar-left">
          <span class="admin-role-badge ${roleClass}">${roleLabel}</span>
          <span class="admin-username">${profile.username}</span>
        </div>
        <div class="admin-bar-actions">
          <button class="btn btn-ghost btn-sm" id="btn-add-member">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Member
          </button>
          <button class="btn btn-ghost btn-sm" id="btn-season">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Season
          </button>
          ${authStore.isPresident()
            ? `
          <button class="btn btn-danger btn-sm" id="btn-reset">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-4"/>
            </svg>
            Reset
          </button>
          `
            : ''}
          <button class="btn btn-ghost btn-sm" id="btn-logout">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Logout
          </button>
        </div>
      </div>
    `;
        bar.querySelector('#btn-add-member')?.addEventListener('click', () => {
            createAddMemberModal().open();
        });
        bar.querySelector('#btn-season')?.addEventListener('click', () => {
            createSeasonModal().open();
        });
        bar.querySelector('#btn-reset')?.addEventListener('click', () => {
            createResetModal().open();
        });
        bar.querySelector('#btn-logout')?.addEventListener('click', () => {
            authStore.logout();
            showToast({ message: 'Signed out', type: 'info' });
        });
    };
    render();
    authStore.subscribe(render);
    return bar;
}
// ── Add Member Modal ──────────────────────────────────────────────────────────
function createAddMemberModal() {
    const content = document.createElement('div');
    let isLoading = false;
    const render = () => {
        content.innerHTML = `
      <div class="add-form">
        <div class="form-group">
          <label class="form-label" for="add-name">Member Name</label>
          <input class="form-input" id="add-name" placeholder="e.g. Alex Johnson" ${isLoading ? 'disabled' : ''} />
          <span class="form-error" id="name-error"></span>
        </div>
        <div class="form-group">
          <label class="form-label" for="add-credits">Starting Credits</label>
          <input class="form-input" id="add-credits" type="number" min="0" placeholder="0" ${isLoading ? 'disabled' : ''} />
          <span class="form-error" id="credits-error"></span>
        </div>
        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancel-btn">Cancel</button>
          <button class="btn btn-primary" id="submit-btn" ${isLoading ? 'disabled' : ''}>
            ${isLoading ? 'Adding...' : 'Add Member'}
          </button>
        </div>
      </div>
    `;
        const nameInput = content.querySelector('#add-name');
        const creditsInput = content.querySelector('#add-credits');
        content.querySelector('#cancel-btn')?.addEventListener('click', () => modal.close());
        content.querySelector('#submit-btn')?.addEventListener('click', async () => {
            const name = nameInput.value.trim();
            const creditsVal = creditsInput.value || '0';
            const nameErr = validateName(name);
            const creditsErr = validateCredits(creditsVal);
            const showErr = (id, msg) => {
                const el = content.querySelector(`#${id}`);
                if (el)
                    el.textContent = msg ?? '';
            };
            showErr('name-error', nameErr);
            showErr('credits-error', creditsErr);
            if (nameErr || creditsErr)
                return;
            isLoading = true;
            render();
            try {
                const result = await addMember(name, parseInt(creditsVal));
                leaderboardStore.addMember(result.member);
                showToast({ message: `${result.member.name} added!`, type: 'success' });
                modal.close();
            }
            catch (err) {
                isLoading = false;
                showToast({ message: err.message ?? 'Failed to add member', type: 'error' });
                render();
            }
        });
    };
    render();
    const modal = createModal({ title: 'Add Member', content });
    return modal;
}
// ── Edit Credits Modal ────────────────────────────────────────────────────────
export function createEditCreditsModal(member, onUpdate) {
    const content = document.createElement('div');
    let isLoading = false;
    const render = () => {
        const deltaVal = (content.querySelector('#edit-delta'))?.value ?? '';
        const delta = parseInt(deltaVal) || 0;
        const newCredits = Math.max(0, member.credits + delta);
        const isPositive = delta > 0;
        const isNegative = delta < 0;
        content.innerHTML = `
      <div class="edit-form">
        <div class="edit-member-preview">
          <span class="edit-member-name">${escapeHtml(member.name)}</span>
          <span class="edit-current-credits">Current: <span>${member.credits.toLocaleString()}</span></span>
        </div>
        <div class="form-group">
          <label class="form-label" for="edit-delta">Credit Change (+/-)</label>
          <input
            class="form-input"
            id="edit-delta"
            type="number"
            placeholder="e.g. +50 or -20"
            value="${deltaVal}"
            ${isLoading ? 'disabled' : ''}
          />
          <span class="form-error" id="delta-error"></span>
        </div>
        <div class="form-group">
          <label class="form-label" for="edit-reason">Reason (optional)</label>
          <input class="form-input" id="edit-reason" placeholder="e.g. Tournament win" ${isLoading ? 'disabled' : ''} />
        </div>
        <div class="delta-preview ${isPositive ? 'positive' : isNegative ? 'negative' : ''}">
          <span>New total:</span>
          <span class="new-value">${newCredits.toLocaleString()} credits</span>
          ${delta !== 0 ? `<span style="font-size:0.8125rem">(${isPositive ? '+' : ''}${delta})</span>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancel-btn">Cancel</button>
          <button class="btn btn-primary" id="submit-btn" ${isLoading ? 'disabled' : ''}>
            ${isLoading ? 'Saving...' : 'Update Credits'}
          </button>
        </div>
      </div>
    `;
        const deltaInput = content.querySelector('#edit-delta');
        deltaInput.addEventListener('input', () => {
            // Live preview re-render
            const val = parseInt(deltaInput.value) || 0;
            const preview = content.querySelector('.delta-preview');
            const nc = Math.max(0, member.credits + val);
            preview.className = `delta-preview ${val > 0 ? 'positive' : val < 0 ? 'negative' : ''}`;
            preview.innerHTML = `
        <span>New total:</span>
        <span class="new-value">${nc.toLocaleString()} credits</span>
        ${val !== 0 ? `<span style="font-size:0.8125rem">(${val > 0 ? '+' : ''}${val})</span>` : ''}
      `;
        });
        content.querySelector('#cancel-btn')?.addEventListener('click', () => modal.close());
        content.querySelector('#submit-btn')?.addEventListener('click', async () => {
            const delta = parseInt(deltaInput.value);
            const reason = (content.querySelector('#edit-reason'))?.value?.trim();
            const deltaErr = validateDelta(String(deltaInput.value));
            const errorEl = content.querySelector('#delta-error');
            if (errorEl)
                errorEl.textContent = deltaErr ?? '';
            if (deltaErr)
                return;
            isLoading = true;
            render();
            try {
                const result = await updateCredits(member.id, delta, reason || undefined);
                leaderboardStore.updateMember(result.member);
                showToast({
                    message: `Credits updated: ${delta > 0 ? '+' : ''}${delta} for ${member.name}`,
                    type: 'success',
                });
                onUpdate?.();
                modal.close();
            }
            catch (err) {
                isLoading = false;
                showToast({ message: err.message ?? 'Failed to update credits', type: 'error' });
                render();
            }
        });
    };
    render();
    const modal = createModal({ title: 'Edit Credits', content });
    return modal;
}
// ── Season Modal ──────────────────────────────────────────────────────────────
function createSeasonModal() {
    const content = document.createElement('div');
    const { season } = leaderboardStore.getState();
    let isLoading = false;
    const render = () => {
        content.innerHTML = `
      <div class="season-form">
        <p style="font-size:0.875rem;color:var(--color-text-muted);line-height:1.6">
          Rename the current season or create a new one. Creating a new season
          will archive the current leaderboard.
        </p>
        <div class="form-group">
          <label class="form-label" for="season-title">Season Title</label>
          <input
            class="form-input"
            id="season-title"
            placeholder="e.g. Season 2"
            value="${season?.title ?? ''}"
            ${isLoading ? 'disabled' : ''}
          />
          <span class="form-error" id="season-error"></span>
        </div>
        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancel-btn">Cancel</button>
          <button class="btn btn-ghost" id="rename-btn" ${isLoading ? 'disabled' : ''}>
            ${isLoading ? 'Saving...' : 'Rename'}
          </button>
          ${authStore.isPresident()
            ? `<button class="btn btn-primary" id="new-season-btn" ${isLoading ? 'disabled' : ''}>
              New Season
            </button>`
            : ''}
        </div>
      </div>
    `;
        const titleInput = content.querySelector('#season-title');
        content.querySelector('#cancel-btn')?.addEventListener('click', () => modal.close());
        content.querySelector('#rename-btn')?.addEventListener('click', async () => {
            const title = titleInput.value.trim();
            if (!title) {
                const el = content.querySelector('#season-error');
                if (el)
                    el.textContent = 'Title is required';
                return;
            }
            if (!season)
                return;
            isLoading = true;
            render();
            try {
                const result = await updateSeason(season.id, title);
                leaderboardStore.setSeason(result.season);
                showToast({ message: 'Season renamed', type: 'success' });
                modal.close();
            }
            catch (err) {
                isLoading = false;
                showToast({ message: err.message ?? 'Failed to rename season', type: 'error' });
                render();
            }
        });
        content.querySelector('#new-season-btn')?.addEventListener('click', async () => {
            const title = titleInput.value.trim();
            if (!title) {
                const el = content.querySelector('#season-error');
                if (el)
                    el.textContent = 'Title is required';
                return;
            }
            const confirmed = window.confirm('Create a new season? The current leaderboard will be archived and a fresh one will start.');
            if (!confirmed)
                return;
            isLoading = true;
            render();
            try {
                const result = await createSeason(title);
                leaderboardStore.setSeason(result.season);
                leaderboardStore.reset();
                showToast({ message: `${result.season.title} started!`, type: 'success' });
                modal.close();
            }
            catch (err) {
                isLoading = false;
                showToast({ message: err.message ?? 'Failed to create season', type: 'error' });
                render();
            }
        });
    };
    render();
    const modal = createModal({ title: 'Manage Season', content });
    return modal;
}
// ── Reset Modal ───────────────────────────────────────────────────────────────
function createResetModal() {
    const content = document.createElement('div');
    let isLoading = false;
    const render = () => {
        content.innerHTML = `
      <div class="danger-zone">
        <p>
          This will permanently delete <strong>all members</strong> from the current
          season. This action cannot be undone. Only Presidents can perform this action.
        </p>
        <div class="modal-actions">
          <button class="btn btn-ghost" id="cancel-btn">Cancel</button>
          <button class="btn btn-danger" id="confirm-btn" ${isLoading ? 'disabled' : ''}>
            ${isLoading ? 'Resetting...' : 'Yes, Reset Leaderboard'}
          </button>
        </div>
      </div>
    `;
        content.querySelector('#cancel-btn')?.addEventListener('click', () => modal.close());
        content.querySelector('#confirm-btn')?.addEventListener('click', async () => {
            isLoading = true;
            render();
            try {
                await resetLeaderboard();
                leaderboardStore.reset();
                showToast({ message: 'Leaderboard reset', type: 'success' });
                modal.close();
            }
            catch (err) {
                isLoading = false;
                showToast({ message: err.message ?? 'Failed to reset', type: 'error' });
                render();
            }
        });
    };
    render();
    const modal = createModal({ title: '⚠️ Reset Leaderboard', content });
    return modal;
}
function escapeHtml(str) {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}
